/**
 * AI Exam question selection — 50 personalised questions
 * 60% from weak topics (accuracy < 70%), 40% from strong topics
 * Weighted by weakness; uses TEST_QUESTION_MAP for all 32 topics
 */

import type { Question } from '../../types';
import { PRACTICE_TEST_IDS } from '../../services/aiExamUnlockService';

const WEAK_THRESHOLD = 70;
const TOTAL_QUESTIONS = 50;
const WEAK_QUESTIONS = 30;
const STRONG_QUESTIONS = 20;

export type TopicScore = {
  topicId: string;
  accuracy: number;
  /** true when the user has actually practised this topic (real Supabase data exists) */
  hasRealData?: boolean;
};

export function buildAIExamQuestions(
  topicScores: TopicScore[],
  questionBank: Record<string, () => Question[]>,
  _language: string,
  isFreeExam: boolean = false
): Question[] {
  const weakTopics = topicScores
    .filter((t) => t.accuracy < WEAK_THRESHOLD)
    .sort((a, b) => a.accuracy - b.accuracy);

  const strongTopics = topicScores
    .filter((t) => t.accuracy >= WEAK_THRESHOLD)
    .sort((a, b) => a.accuracy - b.accuracy);

  const selected: Question[] = [];

  // Free exam with no weak areas: random 50 from all topics equally
  if (isFreeExam && weakTopics.length === 0) {
    const topicIds = strongTopics.length > 0
      ? strongTopics.map((t) => t.topicId)
      : (topicScores.length > 0 ? topicScores.map((t) => t.topicId) : Array.from(PRACTICE_TEST_IDS));
    const perTopic = Math.ceil(TOTAL_QUESTIONS / Math.max(1, topicIds.length));
    for (const topicId of topicIds) {
      const pool = questionBank[topicId]?.() ?? [];
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      selected.push(...shuffled.slice(0, perTopic));
    }
    const result = selected.sort(() => Math.random() - 0.5).slice(0, TOTAL_QUESTIONS);
    return normalizeQuestions(result);
  }

  if (weakTopics.length > 0) {
    const totalWeight = weakTopics.reduce(
      (sum, t) => sum + (WEAK_THRESHOLD - t.accuracy),
      0
    );

    let weakAllocated = 0;

    weakTopics.forEach((topic, index) => {
      const isLast = index === weakTopics.length - 1;
      const weight =
        totalWeight > 0
          ? (WEAK_THRESHOLD - topic.accuracy) / totalWeight
          : 1 / weakTopics.length;
      const allocation = isLast
        ? WEAK_QUESTIONS - weakAllocated
        : Math.round(weight * WEAK_QUESTIONS);

      weakAllocated += allocation;

      const pool = questionBank[topic.topicId]?.() ?? [];
      const shuffled = [...pool].sort(() => Math.random() - 0.5);

      selected.push(...shuffled.slice(0, allocation));
    });
  }

  const weakFilled = selected.length;
  const weakShortfall = WEAK_QUESTIONS - weakFilled;
  const strongTarget = STRONG_QUESTIONS + Math.max(0, weakShortfall);

  if (weakTopics.length > 0 && strongTopics.length > 0) {
    const perTopic = Math.ceil(strongTarget / strongTopics.length);

    strongTopics.forEach((topic) => {
      const pool = questionBank[topic.topicId]?.() ?? [];
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      selected.push(...shuffled.slice(0, perTopic));
    });
  }

  if (weakTopics.length === 0 && strongTopics.length > 0) {
    const perTopic = Math.ceil(TOTAL_QUESTIONS / strongTopics.length);

    strongTopics.forEach((topic) => {
      const pool = questionBank[topic.topicId]?.() ?? [];
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      selected.push(...shuffled.slice(0, perTopic));
    });
  }

  let result = selected.sort(() => Math.random() - 0.5);

  if (result.length < TOTAL_QUESTIONS) {
    const usedIds = new Set(result.map((q) => q.id));
    const allTopics = [...weakTopics, ...strongTopics];
    const topicIds =
      allTopics.length > 0
        ? allTopics.map((t) => t.topicId)
        : Array.from(PRACTICE_TEST_IDS);

    for (const topicId of topicIds) {
      if (result.length >= TOTAL_QUESTIONS) break;
      const pool = (questionBank[topicId]?.() ?? []).filter(
        (q) => !usedIds.has(q.id)
      );
      const shuffled = [...pool].sort(() => Math.random() - 0.5);
      const needed = TOTAL_QUESTIONS - result.length;
      const toAdd = shuffled.slice(0, needed);
      toAdd.forEach((q) => usedIds.add(q.id));
      result = [...result, ...toAdd];
    }
  }

  const trimmed = result.slice(0, TOTAL_QUESTIONS);
  return normalizeQuestions(trimmed);
}

function normalizeQuestions(questions: Question[]): Question[] {
  return questions.map((q) => {
    const raw = q as any;
    return {
      ...q,
      text: q.text || raw.question || '',
      question: raw.question || q.text || '',
      correctAnswerId: q.correctAnswerId || q.answerId || '',
      answerId: q.answerId || q.correctAnswerId || '',
      topic: q.topic || q.subject || 'General',
    };
  });
}

/**
 * Build TopicScore[] for all 32 topics.
 * Uses user_topic_performance when available; defaults to 50% for missing topics.
 */
export function buildTopicScoresFromPerformance(
  performance: { topic_id: string; accuracy: number }[]
): TopicScore[] {
  const byTopic = new Map<string, number>();
  for (const row of performance) {
    byTopic.set(row.topic_id, row.accuracy);
  }

  const result: TopicScore[] = [];
  for (const topicId of PRACTICE_TEST_IDS) {
    const hasRealData = byTopic.has(topicId);
    result.push({
      topicId,
      accuracy: byTopic.get(topicId) ?? 50,
      hasRealData,
    });
  }
  return result;
}
